import { observer } from 'mobx-react';
import { useState } from 'react';
import {
  Drawer,
  Typography,
  Skeleton,
  Alert,
  Empty,
  Tooltip,
  Button,
  Space,
  message,
  Segmented,
} from 'antd';
import {
  ThunderboltOutlined,
  ApiOutlined,
  RobotOutlined,
  CopyOutlined,
  BranchesOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import type {
  ActivityEvent,
  LoadedBriefingDto,
  LoadedBriefingElementDto,
} from '@/api/activity';
import { colors } from '@/theme/tokens';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { RequestTreeView } from './RequestTreeView';

const { Text, Paragraph } = Typography;

const ISSUE_TONE: Record<string, PillTone> = {
  errored: 'red',
  timeout: 'red',
  'context-critical': 'red',
  'context-warning': 'amber',
  slow: 'amber',
  'high-cost': 'amber',
  'oversized-args': 'amber',
  'oversized-response': 'amber',
  compacted: 'violet',
  'deep-chain': 'violet',
};

const KIND_TONE: Record<string, PillTone> = {
  llm: 'ink',
  tool: 'amber',
  consult: 'violet',
};

const KIND_ICON: Record<string, React.ReactNode> = {
  llm: <ThunderboltOutlined />,
  tool: <ApiOutlined />,
  consult: <RobotOutlined />,
};

// Drawer that pulls every event sharing a correlation id (or thread id) so
// the engineer sees the *whole* trace in one place. Top of the drawer shows
// the trace summary (count, total duration, issue rollup); below is the
// chronological event list with full per-event detail.

export const ActivityDrawer = observer(function ActivityDrawer() {
  const { activityStore: store, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [view, setView] = useState<'tree' | 'timeline'>('tree');
  const open = store.openCorrelationId != null || store.openThreadId != null;

  const title = store.openCorrelationId
    ? `Trace · ${store.openCorrelationId}`
    : store.openThreadId
    ? `Thread · ${store.openThreadId}`
    : 'Trace';

  const totalMs = store.drawerEvents.reduce((s, e) => s + e.durationMs, 0);
  const issues = store.drawerEvents.reduce((s, e) => s + (e.issueTags.length > 0 ? 1 : 0), 0);
  const errored = store.drawerEvents.filter((e) => e.errored).length;

  return (
    <Drawer
      open={open}
      onClose={() => store.closeDrawer()}
      size="min(1120px, 100vw)"
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, maxWidth: '100%' }}>
          <BranchesOutlined />
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 13,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 560,
            }}
          >
            {title}
          </span>
        </span>
      }
      destroyOnClose
      styles={{ title: { minWidth: 0 }, body: { padding: 0, background: isDark ? colors.slate[900] : colors.slate[50] } }}
    >
      {store.isLoadingDrawer ? (
        <div style={{ padding: 20 }}>
          <Skeleton active paragraph={{ rows: 5 }} />
        </div>
      ) : store.drawerError ? (
        <div style={{ padding: 20 }}>
          <Alert type="error" showIcon message={store.drawerError} />
        </div>
      ) : store.drawerEvents.length === 0 ? (
        <div style={{ padding: 20 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ fontSize: 13, color: isDark ? colors.slate[400] : colors.slate[500] }}>
                No events found for this trace.
              </span>
            }
          />
        </div>
      ) : (
        <div>
          {/* AI Hypothesis card — top of drawer, the 30-sec answer */}
          <HypothesisCard isDark={isDark} />

          {/* Summary header */}
          <div
            style={{
              padding: '14px 22px',
              background: isDark ? colors.slate[800] : '#FFFFFF',
              borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
              display: 'flex',
              gap: 24,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <SummaryStat label="Events" value={store.drawerEvents.length.toString()} isDark={isDark} />
            <SummaryStat label="Summed event time" value={formatMs(totalMs)} isDark={isDark} />
            <SummaryStat
              label="Issues"
              value={issues.toString()}
              tone={issues > 0 ? 'amber' : 'ink'}
              isDark={isDark}
            />
            <SummaryStat
              label="Errored"
              value={errored.toString()}
              tone={errored > 0 ? 'red' : 'ink'}
              isDark={isDark}
            />
            <span style={{ flex: 1 }} />
            {store.openCorrelationId && (
              <Tooltip title="Copy correlation id">
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    void navigator.clipboard?.writeText(store.openCorrelationId ?? '');
                    message.success('Correlation id copied');
                  }}
                />
              </Tooltip>
            )}
          </div>

          <div style={{ padding: '12px 18px 0' }}>
            <Segmented aria-label="Investigation view" value={view} onChange={(v) => setView(v as typeof view)} options={[
              { label: 'Request tree', value: 'tree' }, { label: 'Timeline', value: 'timeline' },
            ]} />
          </div>
          {view === 'tree' ? <RequestTreeView key={store.openCorrelationId ?? store.openThreadId}
            events={store.drawerEvents} telemetry={store.traceTelemetry} telemetryLoading={store.isLoadingTraceTelemetry}
            singleRequest={store.openCorrelationId != null}
            telemetryError={store.traceTelemetryError} configured={store.appInsightsConfigured}
            renderEvent={(e) => <TraceEventCard event={e} index={store.drawerEvents.indexOf(e)} isDark={isDark} />} /> :
          <div style={{ display: 'flex', flexDirection: 'column', padding: '14px 18px', gap: 10 }}>
            {store.drawerEvents.map((e, i) => (
              <TraceEventCard key={`${e.kind}-${e.id}`} event={e} index={i} isDark={isDark} />
            ))}
          </div>}

          {/* App Insights spans (exceptions + dependencies) for this turn */}
          <TraceTelemetrySection isDark={isDark} />
        </div>
      )}
    </Drawer>
  );
});

// ── Summary stat in the header ─────────────────────────────────────────────

function SummaryStat({
  label,
  value,
  tone = 'ink',
  isDark,
}: {
  label: string;
  value: string;
  tone?: 'ink' | 'amber' | 'red';
  isDark: boolean;
}) {
  const fg =
    tone === 'amber' ? colors.amber[600] : tone === 'red' ? colors.red[600] : colors.ink[600];
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: isDark ? colors.slate[400] : colors.slate[500],
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          fontFamily: "'JetBrains Mono', monospace",
          color: fg,
          fontVariantNumeric: 'tabular-nums',
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ── Single event card (rich detail) ─────────────────────────────────────────

function TraceEventCard({
  event,
  index,
  isDark,
}: {
  event: ActivityEvent;
  index: number;
  isDark: boolean;
}) {
  const tone = KIND_TONE[event.kind];
  const fg =
    tone === 'amber' ? colors.amber[600] : tone === 'violet' ? colors.violet[600] : colors.ink[600];
  const ts = new Date(event.timestamp);

  return (
    <div
      style={{
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderLeft: `3px solid ${fg}`,
        borderRadius: 8,
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10.5,
            fontWeight: 700,
            color: isDark ? colors.slate[500] : colors.slate[400],
            minWidth: 22,
          }}
        >
          {index + 1}
        </span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '2px 9px',
            borderRadius: 999,
            background: `${fg}1A`,
            color: fg,
            fontSize: 10.5,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {KIND_ICON[event.kind]}
          {event.kind}
        </span>
        <Text
          style={{
            fontSize: 12,
            fontFamily: "'JetBrains Mono', monospace",
            color: isDark ? colors.slate[300] : colors.slate[600],
          }}
        >
          {ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </Text>
        <Text
          style={{
            fontSize: 12,
            color: isDark ? colors.slate[400] : colors.slate[500],
            marginLeft: 'auto',
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          {formatMs(event.durationMs)}
        </Text>
      </div>

      {/* Body content per kind */}
      {event.kind === 'llm' ? (
        <LlmBody event={event} isDark={isDark} />
      ) : event.kind === 'tool' ? (
        <ToolBody event={event} isDark={isDark} />
      ) : (
        <ConsultBody event={event} isDark={isDark} />
      )}

      {/* Issue tags */}
      {event.issueTags.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 8 }}>
          {event.issueTags.map((tag) => (
            <StatusPill key={tag} tone={ISSUE_TONE[tag] ?? 'slate'}>
              {tag}
            </StatusPill>
          ))}
        </div>
      )}

      {event.errored && event.errorMessage && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 10px',
            background: isDark ? `${colors.red[700]}33` : colors.red[100],
            border: `1px solid ${colors.red[600]}55`,
            borderRadius: 6,
            fontSize: 11.5,
            fontFamily: "'JetBrains Mono', monospace",
            color: isDark ? colors.red[100] : colors.red[700],
          }}
        >
          {event.errorMessage}
        </div>
      )}
    </div>
  );
}

function LlmBody({ event, isDark }: { event: ActivityEvent; isDark: boolean }) {
  return (
    <div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: isDark ? colors.slate[50] : colors.slate[900],
        }}
      >
        {event.agentName ?? `Agent #${event.agentId}`}
      </div>
      <div
        style={{
          display: 'flex',
          gap: 14,
          marginTop: 4,
          fontSize: 11.5,
          fontFamily: "'JetBrains Mono', monospace",
          color: isDark ? colors.slate[300] : colors.slate[600],
          flexWrap: 'wrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <span>model · {event.modelName ?? '—'}</span>
        <span>in · {(event.inputTokens ?? 0).toLocaleString()}</span>
        <span>out · {(event.outputTokens ?? 0).toLocaleString()}</span>
        {event.cachedTokens ? <span>cached · {event.cachedTokens.toLocaleString()}</span> : null}
        {event.estimatedCostUsd != null ? (
          <span>cost · ${Number(event.estimatedCostUsd).toFixed(4)}</span>
        ) : null}
        {event.percentOfContext != null ? (
          <span>ctx · {Number(event.percentOfContext).toFixed(1)}%</span>
        ) : null}
        {event.toolCallCount ? <span>tools · {event.toolCallCount}</span> : null}
      </div>

      {/* Briefing grounding — added Phase 7 (2026-05-22). Renders only when
          the backend ai.PromptLogBriefingContext audit returned rows for
          this turn, so old turns + non-patient-agent turns hide cleanly. */}
      {event.loadedBriefings && event.loadedBriefings.length > 0 ? (
        <BriefingGroundingPanel briefings={event.loadedBriefings} isDark={isDark} />
      ) : null}
    </div>
  );
}

/**
 * Per-turn briefing grounding panel. One <details> per briefing so the panel
 * stays compact for the common 1–2-briefing case but scales to oncology
 * agents that link a baseline + a regimen overlay + a per-cycle add-on.
 */
function BriefingGroundingPanel({
  briefings,
  isDark,
}: {
  briefings: LoadedBriefingDto[];
  isDark: boolean;
}) {
  // Totals: rendered = elements that actually emitted text; deduped/fault
  // surfaced separately so an SME can see at a glance "5 rendered, 1 deduped"
  // rather than a single inflated count.
  const totals = briefings.reduce(
    (acc, b) => {
      for (const e of b.elements) {
        if (e.isFault) acc.faulted += 1;
        else if (e.dedupedFromCode) acc.deduped += 1;
        else {
          acc.rendered += 1;
          acc.tokens += e.tokenEstimate ?? 0;
        }
      }
      return acc;
    },
    { rendered: 0, deduped: 0, faulted: 0, tokens: 0 },
  );

  const labelColor = isDark ? colors.slate[400] : colors.slate[500];
  const codeColor = isDark ? colors.slate[200] : colors.slate[800];
  const subColor = isDark ? colors.slate[400] : colors.slate[500];

  return (
    <div
      style={{
        marginTop: 10,
        padding: '8px 10px',
        background: isDark ? `${colors.slate[800]}66` : colors.slate[50],
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 6,
        fontSize: 11.5,
        fontFamily: "'JetBrains Mono', monospace",
        color: codeColor,
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 14,
          flexWrap: 'wrap',
          marginBottom: 6,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <span style={{ color: labelColor, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Briefing grounding
        </span>
        <span>rendered · {totals.rendered}</span>
        <span>~tokens · {totals.tokens.toLocaleString()}</span>
        {totals.deduped > 0 ? (
          <span style={{ color: subColor }}>deduped · {totals.deduped}</span>
        ) : null}
        {totals.faulted > 0 ? (
          <span style={{ color: colors.red[600] }}>faulted · {totals.faulted}</span>
        ) : null}
      </div>
      {briefings.map((b) => (
        <BriefingGroundingRow
          key={`${b.briefingCode}-${b.briefingVersion ?? 0}`}
          briefing={b}
          isDark={isDark}
        />
      ))}
    </div>
  );
}

function BriefingGroundingRow({
  briefing,
  isDark,
}: {
  briefing: LoadedBriefingDto;
  isDark: boolean;
}) {
  const rendered = briefing.elements.filter((e) => !e.isFault && !e.dedupedFromCode).length;
  const deduped = briefing.elements.filter((e) => !!e.dedupedFromCode).length;
  const faulted = briefing.elements.filter((e) => e.isFault).length;
  const labelColor = isDark ? colors.slate[400] : colors.slate[500];

  return (
    <details style={{ marginTop: 4 }}>
      <summary
        style={{
          cursor: 'pointer',
          padding: '2px 0',
          color: isDark ? colors.slate[100] : colors.slate[900],
        }}
      >
        <span style={{ fontWeight: 600 }}>{briefing.briefingName}</span>
        <span style={{ color: labelColor }}>
          {' '}
          · {briefing.briefingCode}
          {briefing.briefingVersion != null ? ` v${briefing.briefingVersion}` : ''} · {rendered}{' '}
          rendered
          {deduped > 0 ? `, ${deduped} deduped` : ''}
          {faulted > 0 ? `, ${faulted} faulted` : ''}
        </span>
      </summary>
      <ul
        style={{
          marginTop: 4,
          marginBottom: 0,
          paddingLeft: 18,
          listStyle: 'none',
        }}
      >
        {briefing.elements.map((e, i) => (
          <BriefingGroundingElement key={`${e.elementCode ?? 'fault'}-${i}`} element={e} isDark={isDark} />
        ))}
      </ul>
    </details>
  );
}

function BriefingGroundingElement({
  element,
  isDark,
}: {
  element: LoadedBriefingElementDto;
  isDark: boolean;
}) {
  const subColor = isDark ? colors.slate[400] : colors.slate[500];
  // Lead glyph distinguishes the three row shapes:
  //   ▸ rendered, ⊘ deduped, ⚠ faulted
  // Same color tokens used elsewhere in the drawer for consistency.
  const glyph = element.isFault ? '⚠' : element.dedupedFromCode ? '⊘' : '▸';
  const glyphColor = element.isFault
    ? colors.red[600]
    : element.dedupedFromCode
      ? subColor
      : isDark
        ? colors.slate[200]
        : colors.slate[700];

  return (
    <li style={{ padding: '1px 0' }}>
      <span style={{ color: glyphColor, marginRight: 6 }}>{glyph}</span>
      <span style={{ color: isDark ? colors.slate[100] : colors.slate[900] }}>
        {element.elementDisplayName ?? element.elementCode ?? '(briefing-level fault)'}
      </span>
      <span style={{ color: subColor }}>
        {element.elementCode && element.elementCode !== element.elementDisplayName
          ? ` · ${element.elementCode}`
          : ''}
        {element.elementTier != null ? ` · tier ${element.elementTier}` : ''}
        {element.tokenEstimate > 0 ? ` · ~${element.tokenEstimate} tok` : ''}
        {element.dedupedFromCode ? ` · deduped from ${element.dedupedFromCode}` : ''}
        {element.isFault && element.faultStage ? ` · ${element.faultStage}` : ''}
        {element.isFault && element.faultType ? ` (${element.faultType})` : ''}
      </span>
    </li>
  );
}

function ToolBody({ event, isDark }: { event: ActivityEvent; isDark: boolean }) {
  return (
    <div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          fontFamily: "'JetBrains Mono', monospace",
          color: isDark ? colors.slate[50] : colors.slate[900],
        }}
      >
        {event.pluginName}.{event.methodName}
      </div>
      <div
        style={{
          display: 'flex',
          gap: 14,
          marginTop: 4,
          fontSize: 11.5,
          fontFamily: "'JetBrains Mono', monospace",
          color: isDark ? colors.slate[300] : colors.slate[600],
          flexWrap: 'wrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <span>args · {(event.argumentChars ?? 0).toLocaleString()} ch</span>
        <span>resp · {(event.responseChars ?? 0).toLocaleString()} ch</span>
        {event.modelMaxOutputTokens != null ? (
          <span>cap · {event.modelMaxOutputTokens.toLocaleString()} tok</span>
        ) : null}
        {event.agentName ? <span>agent · {event.agentName}</span> : null}
      </div>
    </div>
  );
}

function ConsultBody({ event, isDark }: { event: ActivityEvent; isDark: boolean }) {
  return (
    <div>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: isDark ? colors.slate[50] : colors.slate[900],
        }}
      >
        {event.primaryAgentName} <span style={{ color: colors.slate[400] }}>→</span>{' '}
        {event.peerAgentName}{' '}
        <span
          style={{
            fontSize: 11,
            color: isDark ? colors.slate[400] : colors.slate[500],
            marginLeft: 4,
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          depth {event.consultationDepth ?? 0}
        </span>
      </div>
      {event.question && (
        <Paragraph
          type="secondary"
          style={{
            fontSize: 12,
            marginTop: 6,
            marginBottom: 0,
            fontStyle: 'italic',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          Q: {event.question}
        </Paragraph>
      )}
      {event.response && (
        <Paragraph
          style={{
            fontSize: 12,
            marginTop: 6,
            marginBottom: 0,
            color: isDark ? colors.slate[200] : colors.slate[700],
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          A: {event.response}
        </Paragraph>
      )}
    </div>
  );
}

function formatMs(ms: number): string {
  if (!ms) return '—';
  if (ms < 1000) return `${ms} ms`;
  const sec = ms / 1000;
  if (sec < 60) return `${sec.toFixed(1)}s`;
  return `${(sec / 60).toFixed(1)}m`;
}

// ── AI Hypothesis card ──────────────────────────────────────────────────────
//
// The 30-second answer at the top of the drawer. Renders the LLM-generated
// root-cause hypothesis with a confidence pill, a suggested next action, and
// the key issue tags it identified. Loading + error states render in place
// so the drawer header layout doesn't shift around.

const HypothesisCard = observer(function HypothesisCard({ isDark }: { isDark: boolean }) {
  const { activityStore: store } = useStore();
  const h = store.hypothesis;
  const loading = store.isLoadingHypothesis;
  const err = store.hypothesisError;
  // The "no analysis yet" state — drawer opened but the engineer hasn't
  // clicked Generate. We show a small CTA instead of running the LLM
  // automatically (see ActivityStore.openTrace — auto-fire was removed
  // because it silently burned tokens on every drawer open).
  const idle = !loading && !err && h == null;

  const bg = isDark
    ? `linear-gradient(135deg, ${colors.violet[700]}33 0%, ${colors.ink[700]}22 100%)`
    : `linear-gradient(135deg, ${colors.violet[50]} 0%, ${colors.ink[50]} 100%)`;
  const border = isDark ? colors.violet[700] + '88' : colors.violet[200];

  const confidenceTone: PillTone =
    h?.confidence === 'high' ? 'green' : h?.confidence === 'low' ? 'amber' : 'ink';

  return (
    <div
      style={{
        padding: '16px 22px 14px',
        background: bg,
        borderBottom: `1px solid ${border}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: isDark ? colors.violet[200] : colors.violet[700],
          }}
        >
          AI hypothesis
        </span>
        {h?.confidence && !h.isEmpty && (
          <StatusPill tone={confidenceTone}>{h.confidence} confidence</StatusPill>
        )}
        {h?.analyzerModel && !h.isEmpty && (
          <span
            style={{
              fontSize: 10,
              fontFamily: "'JetBrains Mono', monospace",
              color: isDark ? colors.slate[400] : colors.slate[500],
            }}
          >
            {h.analyzerModel}
          </span>
        )}
      </div>

      {idle ? (
        // Opt-in state — engineer hasn't asked for analysis yet. One click
        // and we'll spend tokens; we tell them that explicitly so there are
        // no surprises on the cost dashboard.
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={() => void store.generateHypothesisForOpen()}
          >
            Generate AI hypothesis
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Runs an LLM over this trace's events. Costs a small number of tokens; result caches for 5 minutes.
          </Text>
        </div>
      ) : loading ? (
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      ) : err ? (
        <Alert
          type="warning"
          showIcon
          message="Could not generate hypothesis"
          description={err}
          style={{ marginTop: 4 }}
          action={
            <Button size="small" onClick={() => void store.generateHypothesisForOpen()}>
              Retry
            </Button>
          }
        />
      ) : h ? (
        <>
          <div
            style={{
              fontSize: 14,
              lineHeight: 1.55,
              color: isDark ? colors.slate[50] : colors.slate[900],
              fontWeight: 500,
            }}
          >
            {h.hypothesis ?? '—'}
          </div>
          {h.suggestedAction && (
            <div
              style={{
                fontSize: 12.5,
                lineHeight: 1.5,
                marginTop: 8,
                color: isDark ? colors.slate[300] : colors.slate[600],
                fontStyle: 'italic',
              }}
            >
              <span style={{ fontStyle: 'normal', fontWeight: 600 }}>Next: </span>
              {h.suggestedAction}
            </div>
          )}
          {h.keyIssues && h.keyIssues.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
              {h.keyIssues.map((tag) => (
                <StatusPill key={tag} tone={ISSUE_TONE[tag] ?? 'amber'}>
                  {tag}
                </StatusPill>
              ))}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
});

// ── App Insights spans (exceptions + dependencies) for one turn ────────────
//
// Renders below the main event list. App Insights captures things our SQL
// log tables don't — unhandled exception stacks, raw outbound HTTP timings
// (Azure OpenAI, Cosmos, SQL) — so the engineer gets the framework view
// alongside the app view. Soft-fails: when App Insights isn't wired the
// section just collapses, and the drawer keeps working as before.

const TraceTelemetrySection = observer(function TraceTelemetrySection({ isDark }: { isDark: boolean }) {
  const { activityStore: store } = useStore();
  const t = store.traceTelemetry;
  const loading = store.isLoadingTraceTelemetry;
  const err = store.traceTelemetryError;

  // Hide entirely on the thread-view drawer (no single correlation id) and
  // when App Insights returned no spans + no error.
  if (!loading && !err && (!t || (t.exceptions.length === 0 && t.dependencies.length === 0))) {
    return null;
  }

  const sectionBg = isDark ? colors.slate[800] : colors.slate[50];
  const sectionBorder = isDark ? colors.slate[700] : colors.slate[200];

  return (
    <div
      style={{
        marginTop: 8,
        padding: '14px 22px 18px',
        background: sectionBg,
        borderTop: `1px solid ${sectionBorder}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <BranchesOutlined style={{ color: isDark ? colors.slate[300] : colors.slate[600] }} />
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: isDark ? colors.slate[200] : colors.slate[700],
          }}
        >
          App Insights spans
        </span>
        {t?.operationId && (
          <span
            style={{
              fontSize: 10,
              fontFamily: "'JetBrains Mono', monospace",
              color: isDark ? colors.slate[400] : colors.slate[500],
            }}
          >
            op {t.operationId.slice(0, 8)}…
          </span>
        )}
      </div>

      {loading ? (
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      ) : err ? (
        <Alert type="warning" showIcon message="Could not load App Insights data" description={err} />
      ) : (
        <>
          {/* Exceptions block */}
          {t!.exceptions.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 6,
                  color: isDark ? colors.red[500] : colors.red[600],
                }}
              >
                Unhandled exceptions ({t!.exceptions.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {t!.exceptions.map((ex, i) => (
                  <ExceptionRow key={i} ex={ex} isDark={isDark} />
                ))}
              </div>
            </div>
          )}

          {/* Dependencies block */}
          {t!.dependencies.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 6,
                  color: isDark ? colors.slate[200] : colors.slate[700],
                }}
              >
                Dependency calls ({t!.dependencies.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {t!.dependencies.map((d, i) => (
                  <DependencyRow key={i} dep={d} isDark={isDark} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
});

function ExceptionRow({
  ex,
  isDark,
}: {
  ex: { type?: string | null; outerMessage?: string | null; cloudRoleName?: string | null; timestamp: string };
  isDark: boolean;
}) {
  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: 6,
        background: isDark ? colors.slate[900] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.red[700] + '66' : colors.red[100]}`,
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 600, fontSize: 12, color: isDark ? colors.red[500] : colors.red[700] }}>
          {ex.type ?? 'Exception'}
        </span>
        <span style={{ fontSize: 11, color: isDark ? colors.slate[400] : colors.slate[500] }}>
          {new Date(ex.timestamp).toLocaleTimeString()}
        </span>
        {ex.cloudRoleName && (
          <StatusPill tone="ink">{ex.cloudRoleName}</StatusPill>
        )}
      </div>
      {ex.outerMessage && (
        <Paragraph
          style={{
            margin: '6px 0 0',
            fontSize: 12,
            fontFamily: "'JetBrains Mono', monospace",
            color: isDark ? colors.slate[300] : colors.slate[700],
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
          ellipsis={{ rows: 4, expandable: true, symbol: 'show more' }}
        >
          {ex.outerMessage}
        </Paragraph>
      )}
    </div>
  );
}

function DependencyRow({
  dep,
  isDark,
}: {
  dep: { name?: string | null; target?: string | null; resultCode?: string | null; success: boolean; durationMs: number; timestamp: string };
  isDark: boolean;
}) {
  const tone: PillTone = !dep.success ? 'red' : dep.durationMs >= 5000 ? 'amber' : 'ink';
  return (
    <div
      style={{
        padding: '8px 12px',
        borderRadius: 6,
        background: isDark ? colors.slate[900] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        display: 'flex',
        gap: 10,
        alignItems: 'center',
      }}
    >
      <span style={{ fontSize: 11, color: isDark ? colors.slate[400] : colors.slate[500], minWidth: 60 }}>
        {new Date(dep.timestamp).toLocaleTimeString()}
      </span>
      <Text style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace", flex: 1 }} ellipsis>
        {dep.name ?? '(unnamed)'} {dep.target ? `→ ${dep.target}` : ''}
      </Text>
      <StatusPill tone={tone}>{formatMs(dep.durationMs)}</StatusPill>
      {dep.resultCode && (
        <span
          style={{
            fontSize: 10,
            fontFamily: "'JetBrains Mono', monospace",
            color: isDark ? colors.slate[400] : colors.slate[500],
          }}
        >
          {dep.resultCode}
        </span>
      )}
    </div>
  );
}

// Quiet unused-export warnings on Space; kept around for future quick action additions
void Space;
